/**
 * Resolves a product application type (e.g. "floor", "wall", "walls") to its
 * translated label.
 *
 * Application types can originate from CSV imports where vendors type free-form
 * values ("wall", "walls", "floor"...). These are lowercased/space-stripped but
 * not normalized to the canonical enum values, so a stored "wall" would build the
 * i18n key `...FIELDS.WALL` which does not exist — causing the raw key to render.
 *
 * This helper maps known synonyms to the canonical i18n key suffix and falls back
 * to a humanized version of the raw value when no translation exists.
 */

type TranslateFn = (key: string, options?: { defaultValue?: string }) => string;

const APPLICATION_TYPE_KEY_ALIASES: Record<string, string> = {
    floor: 'FLOOR',
    floors: 'FLOOR',
    wall: 'WALLS',
    walls: 'WALLS',
    ceiling: 'CEILING',
    ceilings: 'CEILING',
};

const humanize = (value: string): string =>
    value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

/** Canonical i18n key suffix for an application type, used as a dedupe key. */
function applicationTypeKeySuffix(applicationType: string): string {
    const normalized = applicationType.trim().toLowerCase().replace(/\s+/g, '');
    return APPLICATION_TYPE_KEY_ALIASES[normalized] || normalized.toUpperCase();
}

export function resolveApplicationTypeLabel(t: TranslateFn, applicationType: string): string {
    if (!applicationType) return '';
    return t(`PAGES.NEW_PRODUCT.GENERAL_INFO.FIELDS.${applicationTypeKeySuffix(applicationType)}`, {
        defaultValue: humanize(applicationType),
    });
}

/**
 * Collapses a raw application-types array to its distinct canonical values,
 * preserving first-seen order. Synonyms that map to the same label (e.g. "wall"
 * and "walls") are merged so the UI never shows "Walls / Walls".
 */
export function dedupeApplicationTypes(applicationTypes: string[] = []): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const type of applicationTypes) {
        if (!type) continue;
        const key = applicationTypeKeySuffix(type);
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(type);
    }
    return result;
}

/**
 * Product types that only ever go on walls (ARDE-296: wallpaper). The editor
 * holds such a product to the walls as soon as the type is chosen; the API
 * does the same on save and keeps it out of any floor catalog, so this is the
 * vendor seeing the rule, not the rule itself. A product type is stored as its
 * English name ("Wallpaper") or its slug ("wallpaper").
 */
export const WALL_ONLY_PRODUCT_TYPES = ['wallpaper'];

export function isWallOnlyProductType(productType?: string | null): boolean {
    if (typeof productType !== 'string') return false;
    const normalized = productType.trim().toLowerCase().replace(/[\s-]+/g, '');
    return normalized === 'wallpaper' || normalized === 'wallpapers' || normalized === 'papierpeint';
}
